/*
 * sticky-atc.js — mobile sticky Add-to-Cart bar for product pages.
 *
 * The bar never re-implements add-to-cart. Its button proxies a click to the
 * real <product-form> submit button, so variant selection, product-form.js,
 * quantity, selling plans and the cart drawer all keep working untouched.
 *
 * It also:
 *   - mirrors the live price from #price-<sectionId> (updates on variant change)
 *   - mirrors the real button's label + disabled/sold-out state
 *   - shows itself only once the real button has scrolled out of view
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.submitBtn = document.getElementById(this.dataset.submitId);
        this.priceSource = document.getElementById('price-' + this.dataset.sectionId);
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.addBtn = this.querySelector('[data-sticky-add]');
        this.label = this.querySelector('[data-sticky-label]');

        // Without a real submit button there is nothing to proxy to.
        if (!this.submitBtn || !this.addBtn) return;

        this.addBtn.addEventListener('click', this.onAdd.bind(this));

        this.mirrorPrice();
        this.syncState();

        // Keep price in sync as the variant picker re-renders #price-<id>.
        if (this.priceSource) {
          this.priceObserver = new MutationObserver(() => this.mirrorPrice());
          this.priceObserver.observe(this.priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        // Keep label + disabled state in sync with the real button.
        this.stateObserver = new MutationObserver(() => this.syncState());
        this.stateObserver.observe(this.submitBtn, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        // Show only in the "middle" scroll zone: the real add-to-cart button
        // is off-screen AND we haven't reached the footer yet (so the bar never
        // covers the last footer row at the very bottom of the page).
        this.buttonOffScreen = false;
        this.footerVisible = false;

        if ('IntersectionObserver' in window) {
          this.buttonIO = new IntersectionObserver(
            (entries) => {
              this.buttonOffScreen = !entries[0].isIntersecting;
              this.update();
            },
            { rootMargin: '0px 0px -10% 0px' }
          );
          this.buttonIO.observe(this.submitBtn);

          const footer = document.querySelector('.footer');
          if (footer) {
            this.footerIO = new IntersectionObserver(
              (entries) => {
                this.footerVisible = entries[0].isIntersecting;
                this.update();
              },
              { rootMargin: '0px 0px 40px 0px' }
            );
            this.footerIO.observe(footer);
          }
        } else {
          this.toggle(true);
        }
      }

      update() {
        this.toggle(this.buttonOffScreen && !this.footerVisible);
      }

      onAdd(event) {
        event.preventDefault();
        if (this.isDisabled()) return;
        // Trigger the genuine add-to-cart flow.
        this.submitBtn.click();
      }

      isDisabled() {
        return (
          this.submitBtn.disabled ||
          this.submitBtn.getAttribute('aria-disabled') === 'true'
        );
      }

      mirrorPrice() {
        if (this.priceSource && this.stickyPrice) {
          this.stickyPrice.innerHTML = this.priceSource.innerHTML;
        }
      }

      syncState() {
        const disabled = this.isDisabled();
        this.addBtn.disabled = disabled;
        const sourceLabel = this.submitBtn.querySelector('span');
        if (sourceLabel && this.label) {
          const text = sourceLabel.textContent.trim();
          if (text) this.label.textContent = text;
        }
      }

      toggle(show) {
        if (show) {
          this.removeAttribute('hidden');
        } else {
          this.setAttribute('hidden', '');
        }
      }

      disconnectedCallback() {
        if (this.buttonIO) this.buttonIO.disconnect();
        if (this.footerIO) this.footerIO.disconnect();
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.stateObserver) this.stateObserver.disconnect();
      }
    }
  );
}
